import { Component, type ErrorInfo, type ReactNode } from 'react';
import { t } from '@/i18n';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last line of defence against a blank white page.
 *
 * A render error anywhere below this — a malformed stored session, a chord id
 * that no longer exists, a browser API behaving unexpectedly — should cost the
 * player the screen they were on, not the whole app. The reload button is a
 * plain anchor to `/` so it works even if the router itself is what broke.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Nothing is sent anywhere: this app has no backend and no analytics.
    console.error('[ui] unhandled render error', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="crash" role="alert">
        <div className="crash__card">
          <p className="crash__title">{t('error.title')}</p>
          <p className="crash__hint">{t('error.hint')}</p>
          <a className="btn btn--primary crash__action" href="/">
            {t('error.restart')}
          </a>
          {import.meta.env.DEV && <pre className="crash__detail">{String(error.stack ?? error)}</pre>}
        </div>
      </div>
    );
  }
}
